import Box from '@mui/material/Box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import { useNavigate } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import CreditIcon from "@mui/icons-material/CreditScore";
import BackendPaginationTable from 'common/Table/TableWithPagination';
import CreditNoteSearchHeader from 'components/SalesCreditNotes/SalesCreditNotesSearchHeader';
import Loader from 'layout/Loader';
import { useGetCreditNoteService } from 'subModule/src/services/invoice/useInvoices';
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
interface SearchFormData {
    [key: string]: string | any;
}
export default function SalesCreditNotesListing() {
    const navigate = useNavigate();
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['creditNoteNo']);
    const [search, setSearch] = useState<any>({});
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
    const [dropdownPage, setDropdownPage] = useState<number>(1);
    const [searchLoading, setSearchLoading] = useState<string | null>(null);
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ creditNoteNo: '', vehiclePlate: '', vehicleVin: '', clientName: '', stationId: 0, stationName: '', start: null, end: null });
    const { data: tableData, isFetching, refetch } = useGetCreditNoteService({ pageNumber: pageNo, size: pageSize, ...search });
    const { data: stationData, refetch: stationRefetch } = useGetStationDropdownService({ pageNumber: dropdownPage, size: 50, name: dropdownSearchTerm });
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    useEffect(() => {
        stationRefetch();
    }, []);
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    useEffect(() => {
        if (loading) handleSubmitSearch();
    }, [searchFormData.start, searchFormData.end]);
    const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => {
        if(action === 'searchIt') {
            setDropdownPage(1);
            setDropdownSearchTerm(search);
            if(search) setSearchLoading(typeName);
        } else {
            setDropdownPage(dropdownPage + 1);
            setSearchLoading(typeName);
        }
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if(typeName === 'stations') stationRefetch().finally(() => setSearchLoading(null));
        }, 300);
    }
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const showRowView = (row: any) => navigate('/sales-credit-note/' + row.id + '/view');
    const clearSearch = () => {
        setFilter(['creditNoteNo']);
        setSearchFormData({ creditNoteNo: '', vehiclePlate: '', vehicleVin: '', clientName: '', stationId: 0, start: null, end: null });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        const selectedFilters = typeof value === 'string' ? value.split(',') : value;
        setFilter(selectedFilters);
        setSearchFormData((prevFormData) => {
            const updatedFormData = Object.fromEntries(
                Object.keys(prevFormData).map((key) => [
                key,
                value.includes(key)
                    ? prevFormData[key]
                    : typeof prevFormData[key] === "string"
                    ? ""
                    : 0,
                ])
            );
            if (selectedFilters.includes('stationId') && prevFormData.stationName)
            updatedFormData.stationName = prevFormData.stationName;
            if (selectedFilters.includes('dateInterval')) {
                updatedFormData.start = prevFormData.start;
                updatedFormData.end = prevFormData.end;
            }
            return updatedFormData;
        });
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = field === "stationId" ? value ? parseInt(value.value) || 0 : 0 : value;
        if(field === 'dateInterval') {
            setSearchFormData((prevData) => ({
                ...prevData,
                start: value[0],
                end: value[1]
            }));
            if ((value[0] && value[1]) || ((value[0] === null) && (value[1] === null))) {
                setLoading(true);
            }
        } else {
            setSearchFormData((prevData) => ({
                ...prevData,
                [field]: val,
                ...(field === 'stationId' && value?.label ? { stationName: value.label } : {}),
            }));
        }
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        const { start, end, ...rest } = searchFormData;
        if (start && end) {
            filteredSearch.start = start;
            filteredSearch.end = end;
        } else {
            // ❗ If one or both missing → remove both
            setSearchFormData((prev) => ({
                ...prev,
                start: null,
                end: null,
            }));
        }
        Object.entries(rest).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
            }
        });
        setSearch(filteredSearch);
        setPageNo(1);
        setPageSize(pageSize);
    };
    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/sales-credit-note/create')}
                heading={t('creditNotes')}
                icon={<CreditIcon sx={{ fill: "#000", width: "30px", height: "30px", marginTop: "3px", }} />}
                btnText={"create"}
                btnType={"create"}
                showCreateBtn={checkPrivileges("ROLE_CREDITNOTE_CREATE")}
            />
            {(isFetching || loading) ? <Loader /> : <BackendPaginationTable
                columns={[
                    { id: 'creditNoteNumber', label: 'creditNoteNo', numeric: false },
                    { id: 'referenceNumber', label: 'referenceNo', numeric: false },
                    { id: 'amount', label: 'amount', numeric: false },
                    { id: 'vehiclePlateNo', label: 'plateNumber', numeric: false },
                    { id: 'vehicleVin', label: 'vin', numeric: false },
                    { id: 'clientAttributes', label: 'Client', numeric: false },
                    { id: 'stationName', label: 'station', numeric: false },
                    { id: 'creditNoteDate', label: 'date', numeric: false }
                ]}
                data={tableData?.pages[0]?.data?.content || []}
                pageNo={pageNo}
                pageSize={pageSize}
                tableData={tableData?.pages[0]?.data || {}}
                showCheckbox={false}
                showActions={true}
                enableSorting={true}
                showHeader={true}
                showSearch={true}
                showFilter={true}
                showCreateButton={true}
                handleChangePage={handleChangePage}
                handleChangeRows={handleChangeRows}
                rowClick={checkPrivileges('ROLE_CREDITNOTE_VIEW')}
                showRowView={showRowView}
                loadingSearch={false}
                headerComponent={
                  <Box className="table-header-search">
                    <CreditNoteSearchHeader
                        searchFormData={searchFormData}
                        handleChangeFilter={handleChangeFilter}
                        handleChangeSearch={handleChangeSearch}
                        handleSubmitSearch={handleSubmitSearch}
                        clearSearch={clearSearch}
                        stationData={stationData?.pages[0]?.data?.content || []}
                        stationResponse={stationData || {}}
                        handleDropdownActions={handleDropdownActions}
                        {...{ filter, search, dropdownPage, searchLoading, dropdownSearchTerm, setDropdownSearchTerm, setDropdownPage }}
                    />
                  </Box>
                }
            />}
        </Box>
    )
}